import React, { useRef } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { ArrowRight, Milk, Link as LucideLink, Leaf, Utensils } from 'lucide-react';
import { Button } from './Button';
import TextType from './TextType';
import { Link } from 'react-router-dom';

const Card = ({ 
  src, 
  index, 
  progress, 
  total 
}: { 
  src: string; 
  index: number; 
  progress: MotionValue<number>; 
  total: number;
}) => {
  // Range for this card's specific active phase
  // We divide the total scroll range (0-1) into segments
  const step = 1 / total;
  const start = index * step;
  const end = (index + 1) * step;
  
  // Logic:
  // 1. Entrance/Stacking: Before 'start', card is stacking up (scaling from ~0.9 to 1)
  // 2. Active: At 'start', card is front and center (scale 1, y 0)
  // 3. Exit: After 'start' (going towards 'end'), card moves UP (y goes negative)
  
  // SCALE: changing from 0.8 -> 1.0 as it approaches its active start time
  // We want it to be scale 1 when progress == start
  // We want it to be scale 0.9 when progress == start - step (previous card's start)
  const scale = useTransform(
    progress,
    [start - step, start], 
    [0.9, 1]
  );

  // OPACITY: Optional, but helps stack look cleaner
  // Fade in slightly as it becomes active? Or kept fully opaque?
  // Stacking elements usually look better if fully opaque but maybe shadowed.
  // Let's keep opacity simple for now.

  // Y-POSITION (Exit animation)
  // When progress > start, the card should start moving up.
  // By 'end', it should be fully off screen or significantly up.
  const y = useTransform(
    progress,
    [start, end],
    ['0%', '-100%']
  );

  // Dynamic Z-Index to ensure proper layering
  const zIndex = total - index;

  return (
    <motion.div
      whileHover={{ 
        scale: 1.05, 
        rotateY: 10,
        boxShadow: '0 25px 50px -12px rgba(202, 17, 103, 0.25)' 
      }}
      style={{
        scale,
        y: index === total - 1 ? 0 : y, 
        zIndex,
        top: 'auto',
        perspective: 1000
      }}
      className="absolute w-full aspect-[4/5] max-h-[350px] md:max-h-[500px] max-w-[280px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-[500px] mx-auto rounded-[20px] md:rounded-[30px] overflow-hidden shadow-2xl origin-bottom transition-shadow duration-300"
    >
      <motion.img
        src={src}
        alt={`Technology ${index + 1}`}
        className="w-full h-full object-cover"
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.5 }}
      />
    </motion.div>
  );
};

export const TechnologySection: React.FC = () => {
  const containerRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });

  const images = [
    "/Images/tech/tech_1.webp",
    "/Images/tech/tech_2.webp",
    "/Images/tech/tech_3.webp",
    "/Images/tech/tech_4.webp",
    "/Images/h_machine.webp"
  ];

  return (
    <section 
      ref={containerRef} 
      className="relative bg-white" 
      id="technology"
      style={{ height: `${images.length * 100}vh` }}
    >
      <div className="container mx-auto px-4 h-full">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-20 items-start h-full">
          {/* Left Column: Text Content */}
          {/* Mobile: Static (scrolls away). Desktop: Sticky (pinned). */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center py-10 lg:py-20 lg:sticky lg:top-0 lg:h-screen z-10 bg-white lg:bg-transparent">
            <h2 className="font-heading font-bold text-[24px] sm:text-[32px] md:text-[52px] leading-[1.2] text-text-main mb-4 md:mb-8 lg:mb-12 text-center lg:text-left">
              <TextType
                baseOpacity={0}
                enableBlur={true}
                baseRotation={5}
                blurStrength={10}
                size="lg"
                duration={1}
                textClassName="font-heading font-bold text-[24px] sm:text-[32px] md:text-[52px] leading-[1.2] text-text-main"
              >
                Our technology and production power.
              </TextType>
            </h2>
            <p className="font-instrument font-medium text-[14px] sm:text-[16px] md:text-[20px] leading-[1.4] md:leading-[1.6] text-[#000000] mb-6 md:mb-10 lg:mb-16 text-center lg:text-left">
              Advanced printing, precision die-cutting, and high-speed finishing — engineered for consistent, flawless cartons.
            </p>

            {/* Features: Grid on mobile to save height, List on desktop */}
            <div className="grid grid-cols-2 lg:flex lg:flex-col gap-3 lg:gap-6 mb-6 md:mb-10 lg:mb-16">
              {[
                { icon: Milk, text: 'High-Precision Printing' },
                { icon: LucideLink, text: 'Advanced Die-Cutting' },
                { icon: Leaf, text: 'Sustainable Finishing' },
                { icon: Utensils, text: 'Food-Grade Finishing' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 lg:gap-3 justify-center lg:justify-start">
                  <item.icon className="text-text-muted flex-shrink-0" size={18} strokeWidth={1.5} />
                  <span className="font-instrument font-medium text-[12px] sm:text-[16px] md:text-[23.1px] leading-tight text-text-main">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>

            <Link to="/technology" className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start group">
              <Button className="w-full sm:w-auto bg-[linear-gradient(88.69deg,#CA1167_-1.27%,#F47749_98.89%)] text-white rounded-full px-6 py-3 md:px-8 md:py-4 h-auto font-instrument font-medium text-[12px] md:text-[16px] leading-[16px] group-hover:shadow-lg group-hover:scale-105 transition-all duration-300">
                Know More About Our Machinery
              </Button>
              <div className="cursor-target w-[40px] h-[40px] md:w-[50px] md:h-[50px] rounded-full bg-[linear-gradient(88.69deg,#CA1167_-1.27%,#F47749_98.89%)] flex items-center justify-center text-white group-hover:shadow-lg group-hover:scale-105 transition-all duration-300 hidden sm:flex">
                 <ArrowRight size={20} />
              </div>
            </Link>
          </div>

          {/* Right Column: Animated Card Stack */}
          {/* Always sticky so it stays in view while driving animation */}
          <div className="w-full lg:w-1/2 sticky top-0 h-screen flex items-center justify-center overflow-hidden">
            <div className="relative w-full h-[600px] lg:h-[800px] flex items-center justify-center">
              {images.map((src, index) => (
                <Card 
                  key={index}
                  src={src}
                  index={index}
                  progress={scrollYProgress}
                  total={images.length}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
